import { BreakpointState } from "@angular/cdk/layout";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import isEqual from "lodash-es/isEqual";
import { distinctUntilChanged, Subscription } from "rxjs";

import { LARGE, SMALL } from "@hermes/aphrodite/call-to-action";
import {
    BackgroundColor,
    TRANSPARENT_COLOR,
} from "@hermes/aphrodite/expansion-panel";

import {
    GridFacade,
    GridFilter,
    GridFilterGroup,
    GridSortByOption,
    PriceRange,
    setSwitchFilterActive,
    Sort,
} from "@hermes/states/grid-filters-state";
import { TrayFacade } from "@hermes/states/tray";

import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

export enum TypeFilter {
    LIST = "list",
    COLORS = "colors",
    GRID = "grid",
    SLIDER = "slider",
}

@Component({
    selector: "h-grid-filters",
    templateUrl: "./grid-filters.component.html",
    styleUrl: "./grid-filters.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridFiltersComponent implements OnInit, OnDestroy {
    public gridFilterGroups: Array<
        GridFilterGroup & { counterChecked: number }
    > = [];
    public toggleFilterGroupCollapsed: { [key: string]: boolean } = {};
    public transparentColor: BackgroundColor = TRANSPARENT_COLOR;
    public typeFilter = TypeFilter;
    public priceRange!: PriceRange;
    public hasSelectedFilters$ = this.gridFacade.hasSelectedFilters$;
    public isDefaultSortBy: boolean = false;
    public readonly sortByName = "sortBy";
    public readonly largeButton = LARGE;
    public readonly smallButton = SMALL;

    public sortOptions: GridSortByOption[] = [
        {
            sort: Sort.relevance,
            label: $localize`:@@hermes-search-result.relevance:Relevance`,
        },
        {
            sort: Sort.priceasc,
            label: $localize`:@@hermes-search-result.price-low:Lowest price`,
        },
        {
            sort: Sort.pricedsc,
            label: $localize`:@@hermes-search-result.price-high:Highest price`,
        },
    ];
    public filterGroupForm = new FormGroup({
        listForm: new FormGroup({}),
        colorAndSizeForm: new FormGroup({}),
    });
    public filterSortByForm = new FormGroup({
        sortBy: new FormControl(this.sortOptions[0]),
    });
    public isMobile: boolean = false;

    public collectionSize?: number = 0;

    public areTotalUpdatedItemsHidden = true;

    private subscription = new Subscription();
    private isFirstLoad: boolean = true;

    constructor(
        private trayFacade: TrayFacade,
        private gridFacade: GridFacade,
        private changeDetector: ChangeDetectorRef,
        private breakpointService: BreakpointService,
    ) {}

    public ngOnInit(): void {
        this.subscription.add(
            this.breakpointService
                .mediumBreakpointObserver()
                .subscribe((value: BreakpointState) => {
                    this.isMobile = !value.matches;
                    this.changeDetector.markForCheck();
                }),
        );
        this.subscription.add(
            this.gridFacade.sortBy$.subscribe((sort: Sort) => {
                const sortOption = this.sortOptions.find(
                    (_sortOption) => _sortOption.sort === sort,
                );
                this.isDefaultSortBy = sortOption?.sort === Sort.relevance;
                this.updateSortByValue(sortOption);
            }),
        );
        this.subscription.add(
            this.gridFacade.filterGroups$
                .pipe(distinctUntilChanged(isEqual))
                .subscribe(
                    (
                        gridFilterGroups: Array<
                            GridFilterGroup & { counterChecked: number }
                        >,
                    ) => {
                        this.gridFilterGroups = [...gridFilterGroups];

                        /* Sort filters to display 'switch' first */
                        this.gridFilterGroups.sort((a, _b) =>
                            a.presentation === "switch" ? -1 : 0,
                        );

                        gridFilterGroups.forEach((filterGroup, index) => {
                            filterGroup.counterChecked =
                                this.getCounterFiltersChecked(
                                    filterGroup.filters,
                                );
                            this.collapsingFilterGroup(filterGroup.name, index);
                            this.initFilters(filterGroup);
                        });
                        if (this.isFirstLoad) {
                            this.toggleFilterGroupCollapsed[this.sortByName] =
                                true;
                            this.isFirstLoad = false;
                        }
                        this.changeDetector.markForCheck();
                    },
                ),
        );
        this.subscription.add(
            this.gridFacade.total$.subscribe((total) => {
                // for first total, hide screen reader
                this.areTotalUpdatedItemsHidden =
                    !this.collectionSize || total === undefined;
                this.collectionSize = total;
                this.changeDetector.markForCheck();
            }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public handleCollapsedChange(filterGroupName: string): void {
        this.toggleFilterGroupCollapsed[filterGroupName] = false;
        for (const key of Object.keys(this.toggleFilterGroupCollapsed).filter(
            (name) => name !== filterGroupName,
        )) {
            if (!this.toggleFilterGroupCollapsed[key]) {
                this.toggleFilterGroupCollapsed[key] = true;
                break;
            }
        }
    }

    public handlePriceChange(
        gridFilterGroup: GridFilterGroup & { counterChecked: number },
        prices: PriceRange,
    ): void {
        if (prices) {
            const isUnchanged =
                prices.valueMaxCurrent === prices.valueMax &&
                prices.valueMinCurrent === prices.valueMin;

            gridFilterGroup.counterChecked = isUnchanged ? 0 : 1;

            isUnchanged
                ? this.gridFacade.clearPriceFilter()
                : this.gridFacade.updateGridFilterPrice(prices);
        }
    }

    public trackByFilterGroup(
        index: number,
        filterGroup: GridFilterGroup,
    ): string {
        return `${index}_${filterGroup?.name}`;
    }

    public trackBySortOption(
        index: number,
        sortOption: GridSortByOption,
    ): string {
        return `${index}_${sortOption?.label}`;
    }

    public onClearFilters(): void {
        this.gridFacade.clearFilters();
        this.gridFacade.clearPriceFilter();
        this.gridFacade.clearSortBy();
        this.filterGroupForm.reset();
        this.gridFilterGroups.forEach((filterGroup, index) => {
            filterGroup.counterChecked = 0;
            this.toggleFilterGroupCollapsed[filterGroup.name] = index !== 0;
        });
        this.toggleFilterGroupCollapsed[this.sortByName] = true;
    }

    public closeTray(): void {
        this.trayFacade.closeTray();
    }

    public switchFilterClicked(
        filterGroup: GridFilterGroup,
        selected: boolean,
    ): void {
        const activeFilter = setSwitchFilterActive(filterGroup, selected);

        this.gridFacade.updateGridFilter(activeFilter, filterGroup);
    }

    public isTypeFilter(presentation: string, typeList: TypeFilter[]): boolean {
        return typeList.includes(presentation as TypeFilter);
    }

    public onRadioChecked(sortOption: GridSortByOption): void {
        this.gridFacade.updateGridSort(sortOption);
    }

    private initPriceRange(gridFilters: GridFilter[]): PriceRange {
        const valueToPrice = ({ value }: GridFilter) =>
            Number.parseInt(value, 10);

        const prices = gridFilters.map(valueToPrice);

        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        return {
            valueMin: minPrice,
            valueMinCurrent: minPrice,
            valueMaxCurrent: maxPrice,
            valueMax: maxPrice,
        };
    }

    private updateSortByValue(sortOption?: GridSortByOption): void {
        if (sortOption) {
            this.filterSortByForm.get(this.sortByName)?.setValue(sortOption);
        }
    }

    private getCounterFiltersChecked(filters: GridFilter[]): number {
        return filters.filter((gridFilter) => gridFilter.selected).length;
    }

    private collapsingFilterGroup(
        filterGroupName: string,
        index: number,
    ): void {
        // First init filter page - collapsed all groupfilter except first one
        if (this.isFirstLoad) {
            this.toggleFilterGroupCollapsed[filterGroupName] = index !== 0;
        }
        // Update filter page - collapsed new groupfilter and not change others
        if (
            this.toggleFilterGroupCollapsed[filterGroupName] === undefined &&
            !this.isFirstLoad
        ) {
            this.toggleFilterGroupCollapsed[filterGroupName] = true;
        }
    }

    private initFilters(filterGroup: GridFilterGroup): void {
        const { filters, presentation, name, priceRange } = filterGroup;

        if (presentation === TypeFilter.SLIDER) {
            this.priceRange = priceRange ?? this.initPriceRange(filters);
        } else {
            const currentFormGroup =
                presentation === TypeFilter.LIST
                    ? this.filterGroupForm.controls.listForm
                    : this.filterGroupForm.controls.colorAndSizeForm;
            filters.forEach((filter) => {
                currentFormGroup.addControl(
                    `${name}_${filter.name}`,
                    new FormControl(filter.selected),
                );
            });
        }
    }
}
